import {MenuItem} from '@material-ui/core';
import {isLoaded} from 'Core/models/Async';
import {QuotingRouteConstants} from 'Quoting/Routes';
import {
  QuoteActionSource,
  prepareForProposal,
  setQuoteActionSource,
  toggleQuotesTabActionModal,
  updateSelectedQuoteAction,
  updateSingleQuoteSelectedForAction,
} from 'Quoting/ducks/quoteActions';
import {safeRenderValue} from 'Quoting/utils/utilities';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import {SelectedQuoteDetails} from '../QuotesTab';
import {QuoteListAction} from './QuoteActionUtils';

interface QuoteActionProps {
  availableActions: ActionItem[];
  className?: string;
  actionMenuBtnVariant?: React.ComponentProps<typeof ActionsMenu>['menuBtn'];
  menuBtnLabel?: React.ReactNode;
  selectedQuotes: SelectedQuoteDetails[];
  source?: QuoteActionSource;
}

export interface ActionItem {
  actionName: QuoteListAction;
  actionLabel: string;
}

export const getQuoteActionHandler = (
  quoteAction: QuoteListAction | undefined,
  selectedQuotes: SelectedQuoteDetails[],
  opportunityRootId: string,
  opportunitRevisionNo: number,
  actionSource: QuoteActionSource
) => {
  switch (quoteAction) {
    case QuoteListAction.PrepareForProposal:
      return () => prepareForProposal(selectedQuotes, opportunityRootId, opportunitRevisionNo);
    case QuoteListAction.DeclineByCompany:
    case QuoteListAction.DeclineByCustomer:
      return () => toggleQuotesTabActionModal(true);
    case QuoteListAction.CopyQuote:
      return () => toggleQuotesTabActionModal(true);
    default:
      return null;
  }
};

export const QuoteActionMenu = ({
  availableActions,
  actionMenuBtnVariant,
  menuBtnLabel,
  className,
  selectedQuotes,
  source,
}: QuoteActionProps) => {
  const {opportunitySummary, selectedOpportunityId} = useSelector((state: ReducerState) => state.opportunity);
  const dispatch = useDispatch();
  const history = useHistory();

  const handleQuoteAction = (actionName: QuoteListAction, actionSource: QuoteActionSource) => {
    dispatch(updateSelectedQuoteAction(actionName));

    if (source === QuoteActionSource.QuoteList) {
      dispatch(updateSingleQuoteSelectedForAction(selectedQuotes[0]));
    }

    if (actionName === QuoteListAction.CreateProposal) {
      dispatch(setQuoteActionSource(actionSource));
      return history.push(QuotingRouteConstants.CREATE_PROPOSAL(selectedOpportunityId));
    }

    const opportunityDetails = isLoaded(opportunitySummary) ? opportunitySummary.Data : null;

    const quoteActionHandler = getQuoteActionHandler(
      actionName,
      selectedQuotes,
      opportunityDetails?._key?.rootId ?? '',
      opportunityDetails?._key?.revisionNo ?? 1,
      actionSource
    );

    if (quoteActionHandler) {
      dispatch(setQuoteActionSource(actionSource));
      dispatch(quoteActionHandler());
    }
  };

  return (
    <ActionsMenu
      className={className}
      ariaLabel="Quote Action Menu"
      menuBtn={actionMenuBtnVariant}
      menuBtnLabel={menuBtnLabel ?? 'Action'}
    >
      {Array.isArray(availableActions) && availableActions.length > 0 ? (
        availableActions.map(({actionLabel, actionName}) => (
          <MenuItem
            id={`quote-action-${actionName}`}
            key={`quote-action-${actionName}`}
            onClick={() => handleQuoteAction(actionName, source ?? QuoteActionSource.QuoteList)}
          >
            {safeRenderValue(actionLabel)}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>No actions available</MenuItem>
      )}
    </ActionsMenu>
  );
};
